import {FC, ReactNode, useMemo} from 'react';
import {styled} from 'styled-components';

import {CadViewerPart} from '@shared/models/cad_model';

import {Flex} from '@shared_web/components/core2/flex';

import {Translate} from '@mavismart_frontend/components/translate';
import {LIGHT_GRAY_COLOR, MAIN_COLOR, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';

interface PartDetailsProps {
  part: CadViewerPart | undefined;
}

/**
 * Details of the selected part.
 *
 * Only fields that actually hold a value are rendered: an absent reference shows nothing rather
 * than an empty row or a placeholder, so the panel never suggests data the manifest does not have.
 */
export const PartDetails: FC<PartDetailsProps> = ({part}) => {
  const rows = useMemo<{key: string; label: ReactNode; value: string}[]>(() => {
    if (part === undefined) {
      return [];
    }
    const entries: {key: string; label: ReactNode; value: string | undefined}[] = [
      {key: 'reference', label: <Translate value="door_3d__reference" />, value: part.reference},
      {key: 'category', label: <Translate value="door_3d__category" />, value: part.category},
      {
        key: 'description',
        label: <Translate value="door_3d__description" />,
        value: part.description,
      },
    ];
    return entries.flatMap(entry =>
      entry.value === undefined || entry.value === ''
        ? []
        : [{key: entry.key, label: entry.label, value: entry.value}]
    );
  }, [part]);

  if (part === undefined) {
    return (
      <Wrapper $empty>
        <EmptyHint>
          <Translate value="door_3d__select_a_part" />
        </EmptyHint>
      </Wrapper>
    );
  }

  return (
    <Wrapper $empty={false}>
      <Header>
        <Indicator />
        <Title>{part.label}</Title>
      </Header>
      {rows.length === 0 ? (
        <NoData>
          <Translate value="door_3d__no_part_data" />
        </NoData>
      ) : (
        <RowsContainer>
          {rows.map(row => (
            <Row key={row.key}>
              <RowLabel>{row.label}</RowLabel>
              <RowValue>{row.value}</RowValue>
            </Row>
          ))}
        </RowsContainer>
      )}
      {!part.hasManifestEntry && (
        <Warning>
          <Translate value="door_3d__part_not_documented" />
        </Warning>
      )}
    </Wrapper>
  );
};

PartDetails.displayName = 'PartDetails';

const Wrapper = styled.div<{$empty: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${p => (p.$empty ? '12px 14px' : '14px 14px')};
  border-radius: 8px;
  border: solid 1px ${p => (p.$empty ? LIGHT_GRAY_COLOR : MAIN_COLOR)}40;
  background-color: ${p => (p.$empty ? 'transparent' : `${MAIN_COLOR}08`)};
  min-width: 0;
  flex-shrink: 0;
  transition: background-color 0.15s, border-color 0.15s;
`;

const EmptyHint = styled.div`
  font-size: 13px;
  color: ${TEXT_MAIN_COLOR}99;
  text-align: center;
  padding: 4px 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Indicator = styled.div`
  width: 4px;
  height: 20px;
  border-radius: 2px;
  background-color: ${MAIN_COLOR};
  flex-shrink: 0;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${TEXT_MAIN_COLOR};
  overflow-wrap: anywhere;
`;

const RowsContainer = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  padding-left: 12px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const RowLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${TEXT_MAIN_COLOR}88;
`;

const RowValue = styled.span`
  font-size: 13px;
  color: ${TEXT_MAIN_COLOR};
  overflow-wrap: anywhere;
`;

const NoData = styled.div`
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR}99;
  padding-left: 12px;
`;

const Warning = styled.div`
  font-size: 12px;
  color: ${MAIN_COLOR};
  overflow-wrap: anywhere;
  padding-left: 12px;
`;
